import React, { createContext, useContext, useEffect, useState } from "react";
import SecureLS from "secure-ls";
import { NotificationDialog } from "./NotificationDialog";
import { requestBackendJson } from "../../../../utils/backendApi";

const NotificationContext = createContext();

export const useNotificationsContext = () => useContext(NotificationContext);

export const NotificationContextProvider = ({ children, userId }) => {
  const [notifications, setNotifications] = useState([]);
  const [currentNotificationIndex, setCurrentNotificationIndex] = useState(0);
  const [dialogOpen, setDialogOpen] = useState(false);
  const resolveTargetUserId = (explicitUserId = null) => {
    if (explicitUserId) {
      return explicitUserId;
    }
    if (userId) {
      return userId;
    }
    const ls = new SecureLS({ isCompression: false });
    return ls.get("userDetails")?.userId || null;
  };

  const fetchNotifications = async (explicitUserId = null) => {
    const targetUserId = resolveTargetUserId(explicitUserId);
    if (!targetUserId) {
      return;
    }

    try {
      const { data: fetchedNotificationsRaw } = await requestBackendJson(
        `/v1/api/notification/all/${encodeURIComponent(String(targetUserId))}`
      );
      const fetchedNotifications = Array.isArray(fetchedNotificationsRaw)
        ? fetchedNotificationsRaw
        : [];

      // Keep backward compatibility with single-recipient notifications and
      // support multi-recipient notifications.
      const relevantNotifications = fetchedNotifications.filter((notification) => {
        const singleRecipient = String(
          notification?.recipientUser?._id || notification?.recipientUser || ""
        ).trim();
        const multiRecipients = Array.isArray(notification?.recipientUsers)
          ? notification.recipientUsers
              .map((entry) => String(entry?._id || entry || "").trim())
              .filter(Boolean)
          : [];

        if (!singleRecipient && multiRecipients.length === 0) {
          return true;
        }
        if (singleRecipient && singleRecipient === String(targetUserId)) {
          return true;
        }
        return multiRecipients.includes(String(targetUserId));
      });

      setNotifications(relevantNotifications);
    } catch (error) {
      console.error("Failed to fetch notifications", error);
    }
  };

  useEffect(() => {
    setDialogOpen(notifications.length > 0);
  }, [notifications]);

  const handleCloseDialog = () => {
    setDialogOpen(false);
    const nextIndex = currentNotificationIndex + 1;
    if (nextIndex < notifications.length) {
      setCurrentNotificationIndex(nextIndex);
      setDialogOpen(true);
    }
  };

  const addNotification = async (notification) => {
    try {
      const { data } = await requestBackendJson(
        "/v1/api/notification/create",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(notification),
        },
        {
          fallbackStatusCodes: [404, 502, 503, 504],
        }
      );
      if (data) {
        setNotifications((prevNotifications) => [...prevNotifications, data]);
      }
    } catch (error) {
      console.error("Failed to create notification", error);
    }
  };

  const dismissNotification = async (notificationIds) => {
    const targetUserId = resolveTargetUserId();
    if (!targetUserId) {
      return;
    }

    try {
      await requestBackendJson(
        "/v1/api/notification/append",
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            userId: targetUserId,
            notificationIds,
          }),
        }
      );
      setNotifications((prevNotifications) =>
        prevNotifications.filter(
          (notification) => !notificationIds.includes(notification._id)
        )
      );
    } catch (error) {
      console.error("Failed to dismiss notifications", error);
    }
  };

  const contextValue = {
    notifications,
    addNotification,
    dismissNotification,
    fetchNotifications,
  };

  return (
    <NotificationContext.Provider
      value={{
        notifications,
        addNotification,
        dismissNotification,
        fetchNotifications,
      }}
    >
      {children}
      {notifications.length > 0 && (
        <NotificationDialog
          open={dialogOpen}
          onClose={handleCloseDialog}
          notifications={notifications}
          onDismiss={dismissNotification}
        />
      )}
    </NotificationContext.Provider>
  );
};
